import { Link } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import type { NamespaceResponse } from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useNamespaces } from '@/api/rpc/hooks/namespaces/useNamespaces';
import { HeaderPageTitle } from '@/context/header-title.context';

/**
 * All of the organization's namespaces (`core_namespaces`). A namespace
 * groups everything a module or flow uses — tasks (which it numbers), task
 * types, workflows and agent definitions — so each row links to an inventory
 * page. Create/edit still live in the task-type, workflow, and agent editors.
 */
export function NamespacesPage() {
  const gt = useGT();
  const { data: namespaces, isLoading, error } = useNamespaces();

  return (
    <div className="mx-auto w-full max-w-4xl space-y-4 p-4">
      <div>
        <HeaderPageTitle>
          <T context="namespaces page title">Namespaces</T>
        </HeaderPageTitle>
        <p className="text-sm text-muted-foreground">
          <T context="namespaces page description">
            One namespace per module or flow: the tasks it numbers, its task
            types, workflows and agents. Open one to see its footprint.
          </T>
        </p>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error instanceof Error ? error.message : String(error)}
        </div>
      ) : null}

      <div className="rounded-md border bg-card">
        {isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          <NamespacesTable namespaces={namespaces ?? []} gt={gt} />
        )}
      </div>
    </div>
  );
}

function NamespacesTable({
  namespaces,
  gt,
}: {
  namespaces: NamespaceResponse[];
  gt: ReturnType<typeof useGT>;
}) {
  if (namespaces.length === 0) {
    return (
      <p className="p-3 text-sm text-muted-foreground">
        <T context="empty state on the namespaces page">
          No namespaces yet. They are created from the task type, workflow, or
          agent editors.
        </T>
      </p>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            {gt('Key', { context: 'namespaces table column' })}
          </TableHead>
          <TableHead>
            {gt('Name', { context: 'namespaces table column' })}
          </TableHead>
          <TableHead>
            {gt('Description', { context: 'namespaces table column' })}
          </TableHead>
          <TableHead className="text-right">
            {gt('Tasks numbered', { context: 'namespaces table column' })}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {namespaces.map((namespace) => (
          <TableRow key={namespace.id}>
            <TableCell>
              <Link
                to="/namespaces/$namespaceId"
                params={{ namespaceId: namespace.id }}
                className="font-mono font-medium underline-offset-2 hover:underline"
              >
                {namespace.key}
              </Link>
              {namespace.isDefault ? (
                <Badge variant="secondary" className="ml-2">
                  <T context="badge on the organization's default namespace">
                    default
                  </T>
                </Badge>
              ) : null}
            </TableCell>
            <TableCell>
              <Link
                to="/namespaces/$namespaceId"
                params={{ namespaceId: namespace.id }}
                className="underline-offset-2 hover:underline"
              >
                {namespace.name}
              </Link>
            </TableCell>
            <TableCell className="max-w-md truncate text-muted-foreground">
              {namespace.description ?? '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {namespace.lastTaskNumber}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
